import * as Sentry from "@sentry/react";
import type { Id } from "@teachloop/backend/_generated/dataModel";
import { useRouter } from "expo-router";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Text, View } from "react-native";
import SubjectIcon from "@/assets/svgs/subject-icon";
import SubjectReviewIcon from "@/assets/svgs/subject-review-icon";
import { useFormatDate } from "@/elements/hooks/use-format-date";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../ui/alert-dialog";
import { Avatar } from "../ui/avatar";
import { Button } from "../ui/button";

export type TutorLessonHistoryItemData = {
  _id: Id<"lessonReports">;
  status: "pending" | "in_progress" | "completed" | "failed" | "pending_review";
  completedAt: number | undefined;
  isAvailable: boolean;
  deliveryMethod: "in_person" | "remote";
  student: {
    firstName: string;
    imageUrl: string | null;
  };
};

type TutorLessonHistoryItemProps = {
  item: TutorLessonHistoryItemData;
  onProcessLessonReport: (args: {
    lessonReportId: Id<"lessonReports">;
    skipRecordings?: boolean;
  }) => Promise<unknown>;
};

const RETRY_DELAYS = [15_000, 30_000];

function TutorLessonHistoryItem({
  item,
  onProcessLessonReport,
}: TutorLessonHistoryItemProps) {
  const fmt = useFormatDate();
  const router = useRouter();

  const [isProcessingLocal, setIsProcessingLocal] = useState(false);
  const [showManualModal, setShowManualModal] = useState(false);
  const [countdown, setCountdown] = useState<number | null>(null);

  const prevStatusRef = useRef<string | null>(null);
  const retryRef = useRef({ active: false, attempt: 0, skipRecordings: false });

  const process = useCallback(
    async (skipRecordings: boolean) => {
      retryRef.current = { active: true, attempt: 0, skipRecordings };

      setIsProcessingLocal(true);

      setCountdown(null);

      try {
        await onProcessLessonReport({
          lessonReportId: item._id,
          skipRecordings,
        });
      } catch (error) {
        Sentry.captureException(error, {
          tags: {
            source: "lesson-report.process",
            lessonReportId: item._id,
            skipRecordings: String(skipRecordings),
            deliveryMethod: item.deliveryMethod,
          },
        });
        retryRef.current.active = false;
        setIsProcessingLocal(false);
      }
    },
    [item._id, onProcessLessonReport],
  );

  const handleProcess = async () => {
    await process(item.deliveryMethod === "in_person");
  };

  const handleManualProcess = async () => {
    setShowManualModal(false);

    await process(true);
  };

  const handleCloseModal = () => {
    setShowManualModal(false);

    retryRef.current = { active: false, attempt: 0, skipRecordings: false };
  };

  useEffect(() => {
    const wasInProgress = prevStatusRef.current === "in_progress";
    const nowFailed = item.status === "pending" || item.status === "failed";

    if (wasInProgress && nowFailed && retryRef.current.active) {
      if (retryRef.current.attempt >= RETRY_DELAYS.length) {
        Sentry.captureMessage("Lesson report processing: retries exhausted", {
          level: "warning",
          tags: {
            source: "lesson-report.retry-exhausted",
            lessonReportId: item._id,
            attempts: String(RETRY_DELAYS.length),
            deliveryMethod: item.deliveryMethod,
          },
        });

        retryRef.current.active = false;

        setIsProcessingLocal(false);

        setShowManualModal(true);

        prevStatusRef.current = item.status;

        return;
      }

      Sentry.captureMessage("Lesson report processing failed, retrying", {
        level: "info",
        tags: {
          source: "lesson-report.retry",
          lessonReportId: item._id,
          attempt: String(retryRef.current.attempt),
          deliveryMethod: item.deliveryMethod,
        },
      });

      const delay = RETRY_DELAYS[retryRef.current.attempt];

      if (delay) {
        setCountdown(Math.ceil(delay / 1000));

        const startTime = Date.now();

        const countdownInterval = setInterval(() => {
          const elapsed = Date.now() - startTime;
          const remaining = Math.max(0, Math.ceil((delay - elapsed) / 1000));

          setCountdown(remaining);

          if (remaining <= 0) {
            clearInterval(countdownInterval);
          }
        }, 1000);

        const retryTimeout = setTimeout(() => {
          clearInterval(countdownInterval);

          setCountdown(null);

          retryRef.current.attempt += 1;

          onProcessLessonReport({
            lessonReportId: item._id,
            skipRecordings: retryRef.current.skipRecordings,
          });
        }, delay);

        prevStatusRef.current = item.status;

        return () => {
          clearInterval(countdownInterval);
          clearTimeout(retryTimeout);
        };
      }
    }

    if (item.status === "pending_review" || item.status === "completed") {
      retryRef.current.active = false;
      setIsProcessingLocal(false);
      setCountdown(null);
    }

    prevStatusRef.current = item.status;
  }, [item.status, item._id, onProcessLessonReport]);

  const renderButton = () => {
    if (countdown !== null) {
      return (
        <Button size="sm" borderLess icon className="py-0" disabled>
          <Button.Icon
            as={SubjectIcon}
            pathClassName="stroke-gray-400 stroke-2"
          />
          <Button.Text size="sm" intent="secondary">
            Retrying in {countdown}s
          </Button.Text>
        </Button>
      );
    }

    if (item.status === "in_progress" || isProcessingLocal) {
      return (
        <Button size="sm" borderLess icon className="py-0" disabled>
          <Button.Icon
            as={SubjectIcon}
            pathClassName="stroke-gray-400 stroke-2"
          />
          <Button.Text size="sm" intent="secondary">
            Processing
          </Button.Text>
        </Button>
      );
    }

    if (item.status === "pending") {
      return (
        <Button
          size="sm"
          borderLess
          icon
          className="py-0"
          onPress={handleProcess}
        >
          <Button.Icon
            as={SubjectIcon}
            pathClassName="stroke-teachLoop-accentPrimary stroke-2"
          />
          <Button.Text size="sm" intent="primary">
            Process
          </Button.Text>
        </Button>
      );
    }

    if (item.status === "failed") {
      return (
        <Button
          size="sm"
          borderLess
          icon
          className="py-0"
          onPress={handleProcess}
        >
          <Button.Icon
            as={SubjectIcon}
            pathClassName="stroke-red-500 stroke-2"
          />
          <Button.Text size="sm" intent="danger">
            Failed • Tap to retry
          </Button.Text>
        </Button>
      );
    }

    return (
      <Button
        size="sm"
        borderLess
        icon
        className="py-0"
        disabled={!item.isAvailable}
        onPress={() => {
          router.navigate(`/(app)/lesson-history/${item._id}`);
        }}
      >
        <Button.Icon
          as={
            item.status === "pending_review" ? SubjectReviewIcon : SubjectIcon
          }
          pathClassName={
            item.status === "pending_review"
              ? "fill-teachLoop-accentPrimary"
              : "stroke-teachLoop-accentPrimary stroke-2"
          }
        />
        <Button.Text size="sm" intent="primary">
          {item.status === "pending_review" ? "Review report" : "View report"}
        </Button.Text>
      </Button>
    );
  };

  return (
    <>
      <View className="flex-row py-3 items-center">
        <View className="flex-row items-center gap-2 flex-1">
          <Avatar size="sm">
            <Avatar.Content
              labelSize="sm"
              size="sm"
              label={item.student.firstName}
              source={
                item.student.imageUrl
                  ? { uri: item.student.imageUrl }
                  : undefined
              }
            />
          </Avatar>

          <Text
            className="font-geologica-medium font-medium text-base text-gray-800 leading-[1.5]"
            numberOfLines={1}
          >
            {item.student.firstName}
          </Text>

          <Text
            className="font-geologica-regular font-normal text-base text-gray-800 leading-[1.5]"
            numberOfLines={1}
          >
            {item.completedAt ? fmt(item.completedAt, "do MMMM") : ""}
          </Text>
        </View>

        {renderButton()}
      </View>

      <AlertDialog open={showManualModal} onOpenChange={handleCloseModal}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Processing failed</AlertDialogTitle>

            <AlertDialogDescription>
              We couldn't automatically generate the lesson report. This may be
              because the recording is still being processed or isn't available
              yet. Would you like to fill in the report details manually?
            </AlertDialogDescription>
          </AlertDialogHeader>

          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onPress={handleManualProcess}>
              Continue manually
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

export default TutorLessonHistoryItem;
